/**
 * Restaurant Dashboard - YUL ON TAP (Mock SSO Landing)
 * 
 * TEMPORARY SSO landing surface for demo/testing purposes.
 * This is the mock destination for SSO redirects from core industries
 * (Restaurant, Automotive, Healthcare).
 * 
 * PURPOSE: Proves routing, authentication, and industry isolation.
 * NOT a functional replacement for the real restaurant portal.
 * Will be removed/bypassed when LIVE_RESTAURANT_URL is connected.
 * 
 * YUL ON TAP is an NFC-based guest engagement and review attribution
 * platform - it tracks tap activity and routes guests to leave reviews,
 * but does NOT manage or edit reviews directly.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import CommandBar from '../components/CommandBar';
import {
  UtensilsCrossed,
  Building2,
  Star,
  TrendingUp,
  BarChart3,
  MessageSquare,
  Activity,
  ChevronRight,
  Plus,
  Car,
  HeartPulse,
} from 'lucide-react';
import { tLang } from '../lib/langHelper';

// Industry display configuration
const INDUSTRY_CONFIG = {
  restaurant: {
    icon: UtensilsCrossed,
    color: '#f97316',
    name: { en: 'Restaurants', fr: 'Restaurants' },
    description: { 
      en: 'Track guest engagement and review attribution',
      fr: 'Suivez l\'engagement des clients et l\'attribution des avis'
    }
  },
  automotive: {
    icon: Car,
    color: '#22c55e',
    name: { en: 'Automotive', fr: 'Automobile' },
    description: {
      en: 'Monitor tap activity and customer engagement',
      fr: 'Suivez l\'activité des taps et l\'engagement client'
    }
  },
  healthcare: {
    icon: HeartPulse,
    color: '#ef4444',
    name: { en: 'Healthcare', fr: 'Santé' },
    description: {
      en: 'Track patient engagement and feedback attribution',
      fr: 'Suivez l\'engagement des patients et l\'attribution des commentaires'
    }
  }
};

// Stats component
const StatCard = ({ title, value, icon: Icon, color, description }) => (
  <Card className="bg-card border-border">
    <CardContent className="flex items-center space-x-4 p-4">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground/70">{description}</p>
        )}
      </div>
    </CardContent>
  </Card>
);

export default function RestaurantDashboard() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, industryPortal } = useAuth();
  const { checkTimeBasedPrompt } = useTheme();
  const [loading, setLoading] = useState(() => {
    // Check auth synchronously on mount
    return !localStorage.getItem('token');
  });

  // Get effective industry from portal or user
  const industry = industryPortal || localStorage.getItem('yul_industry_portal') || user?.industry_type || 'restaurant';
  const config = INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.restaurant;

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    // Check for time-based dark mode prompt
    checkTimeBasedPrompt();
  }, [navigate, checkTimeBasedPrompt]);

  // Mock stats for demo - NFC engagement tracking metrics
  const stats = {
    locations: 12,
    reviewsAttributed: 1847,
    avgRating: 4.6,
    monthlyTaps: 3250,
    conversionRate: 78,
    newAttributions: 124
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Command Bar - Pass effective industry for correct labeling across all industry portals */}
      <CommandBar
        title={tLang(i18n.language, 'Dashboard', 'Tableau de bord', 'Panel de control')}
        subtitle={config.description[i18n.language] || config.description.en}
        workspace={industry}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stats Grid - Tap Engine Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title={tLang(i18n.language, 'Locations', 'Établissements', 'Ubicaciones')}
            value={stats.locations}
            icon={Building2}
            color="#3b82f6"
          />
          <StatCard
            title={tLang(i18n.language, 'Tap Engine Conversions', 'Conversions Tap Engine', 'Tap Engine Conversions')}
            value={stats.reviewsAttributed.toLocaleString()}
            icon={MessageSquare}
            color="#22c55e"
          />
          <StatCard
            title={tLang(i18n.language, 'Avg Rating', 'Note Moyenne', 'Calif. prom.')}
            value={`${stats.avgRating} ★`}
            icon={Star}
            color="#f59e0b"
          />
          <StatCard
            title={tLang(i18n.language, 'Monthly Taps', 'Taps ce mois', 'Taps del mes')}
            value={stats.monthlyTaps.toLocaleString()}
            icon={Activity}
            color="#8b5cf6"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Locations Management */}
          <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center mb-2">
                <Building2 className="w-6 h-6 text-blue-500" />
              </div>
              <CardTitle className="text-foreground flex items-center justify-between">
                {tLang(i18n.language, 'Locations', 'Établissements', 'Ubicaciones')}
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {tLang(i18n.language, 'Manage your locations and NFC tags', 'Gérez vos établissements et tags NFC', 'Manage your locations and NFC tags')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {stats.locations} {tLang(i18n.language, 'active', 'actifs', 'activos')}
                </span>
                <Button size="sm" className="bg-primary hover:bg-primary/90">
                  <Plus className="w-4 h-4 mr-1" />
                  {tLang(i18n.language, 'Add', 'Ajouter', 'Agregar')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tap Engine Cards */}
          <Card className="bg-card border-border hover:border-amber-500/50 transition-colors cursor-pointer">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center mb-2">
                <Star className="w-6 h-6 text-amber-500" />
              </div>
              <CardTitle className="text-foreground flex items-center justify-between">
                {tLang(i18n.language, 'Tap Engine', 'Tap Engine', 'Tap Engine')}
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {tLang(i18n.language, 'Manage your Tap Engine cards and conversions', 'Gérez vos cartes Tap Engine et conversions', 'Manage your Tap Engine cards and conversions')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm">
                <span className="text-amber-500">
                  +{stats.newAttributions} {tLang(i18n.language, 'new', 'nouveaux', 'nuevos')}
                </span>
                <span className="text-muted-foreground">
                  {stats.conversionRate}% {tLang(i18n.language, 'conversion', 'conversion', 'conversión')}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Tap Analytics */}
          <Card className="bg-card border-border hover:border-purple-500/50 transition-colors cursor-pointer">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-purple-500/15 flex items-center justify-center mb-2">
                <BarChart3 className="w-6 h-6 text-purple-500" />
              </div>
              <CardTitle className="text-foreground flex items-center justify-between">
                {tLang(i18n.language, 'Performance', 'Performance', 'Rendimiento')}
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {tLang(i18n.language, 'Monitor tap activity and Tap Engine performance', 'Suivez l\'activité des taps et conversions', 'Monitor tap activity and Tap Engine performance')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2 text-sm">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-green-500">
                  +12% {tLang(i18n.language, 'this month', 'ce mois', 'este mes')}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Tap Engine Activity */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">
              {tLang(i18n.language, 'Tap Engine Activity', 'Activité Tap Engine', 'Tap Engine Activity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { type: 'conversion', text: tLang(i18n.language, '5-star Google conversion', 'Conversion 5 étoiles Google', '5-star Google conversion'), time: '2m', color: 'text-amber-500' },
                { type: 'tap', text: tLang(i18n.language, '15 new taps', '15 nouveaux taps', '15 new taps'), time: '1h', color: 'text-blue-500' },
                { type: 'conversion', text: tLang(i18n.language, '4-star Yelp conversion', 'Conversion 4 étoiles Yelp', '4-star Yelp conversion'), time: '3h', color: 'text-amber-500' },
                { type: 'location', text: tLang(i18n.language, 'New Tap Engine card activated', 'Nouvelle carte Tap Engine activée', 'New Tap Engine card activated'), time: '1d', color: 'text-green-500' },
              ].map((activity, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${activity.color.replace('text-', 'bg-')}`} />
                    <span className="text-foreground">{activity.text}</span>
                  </div>
                  <span className="text-muted-foreground text-sm">{activity.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
